import React, { useEffect } from 'react';
import PasswordTextField from '../../PasswordTextField/PasswordTextField';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { ApiLoginPayload } from '../../../store/services/auth-api';
import { Grid } from '@material-ui/core';
import TextField from '../../TextField/TextField';
import CustomButton from '../../Button/Button';
import { APIErrorInterface, ReduxState } from '../../../store/types';
import { getLoginFieldError } from './helpers';
import { initLoginFormData, useStyles, validate } from './LoginForm.utils';
import { useSelector } from 'react-redux';
import Loader from '../../Loader/Loader';

type LoginFormProps = {
    onSubmitAction: (payload: ApiLoginPayload) => void;
    onRecoveryClick: (login?: string) => void;
    validateOnChange: boolean;
    setSnackbarMessage: (text: string) => void;
    apiErrors?: APIErrorInterface;
    handleLoginChange?: (login: string) => void;
    passedLogin?: string;
};

const LoginForm: React.FC<LoginFormProps> = ({
    onSubmitAction,
    onRecoveryClick,
    setSnackbarMessage,
    validateOnChange = false,
    apiErrors,
    passedLogin,
}) => {
    const classes = useStyles();
    const { t } = useTranslation();

    const inProgress = useSelector<ReduxState>(
        (state) => state.auth.inProgress ?? false,
    );

    const formik = useFormik({
        initialValues: initLoginFormData,
        validate,
        validateOnChange: validateOnChange,
        onSubmit: (values) => {
            const payload = {
                login: values.login,
                password: values.password,
            };

            onSubmitAction(payload);
        },
        enableReinitialize: false,
    });

    const { values, setFieldValue } = formik;

    const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        const { global } = validate(values);
        const anyFieldFilled = !!(values.login || values.password);

        setSnackbarMessage(
            global && anyFieldFilled ? t(`errors:loginForm.${global}`) : '',
        );

        formik.handleSubmit(event);
    };

    const loginErrorMsg = getLoginFieldError(
        'login',
        formik.errors.login,
        apiErrors,
    );
    const passwordErrors = getLoginFieldError(
        'password',
        formik.errors.password,
        apiErrors,
    );
    const handleRecoveryClick = () => {
        onRecoveryClick(values.login);
    };

    useEffect(() => {
        passedLogin && setFieldValue('login', passedLogin);
    }, [passedLogin]);

    return (
        <form onSubmit={handleFormSubmit} autoComplete="off">
            <div className={classes.inputs}>
                <Grid item>
                    <TextField
                        id="login"
                        dataQa="login-user-input"
                        label={t('screens:signIn.loginLabel')}
                        onChange={formik.handleChange}
                        value={formik.values.login.trim()}
                        helperText={
                            loginErrorMsg
                                ? t(`errors:loginForm.${loginErrorMsg}`)
                                : ''
                        }
                        setFieldError={formik.setFieldError}
                        autoComplete={'username'}
                    />
                </Grid>
                <Grid item>
                    <PasswordTextField
                        id="password"
                        dataQa="login-password-input"
                        label={t('screens:signIn.passwordLabel')}
                        onChange={formik.handleChange}
                        value={formik.values.password.trim()}
                        error={
                            passwordErrors
                                ? t(`errors:loginForm.${passwordErrors}`)
                                : ''
                        }
                        setFieldError={formik.setFieldError}
                        autoComplete={'current-password'}
                    />
                </Grid>
            </div>
            <div className={classes.action}>
                <div className={classes.recoveryButtonContainer}>
                    <CustomButton
                        dataQa="login-recovery-button"
                        onClick={handleRecoveryClick}
                        type="button"
                        className={classes.recoveryButton}
                    >
                        {t('screens:signIn.forgotPasswordButton')}
                    </CustomButton>
                </div>
                <CustomButton
                    dataQa="login-submit-button"
                    primary
                    type="submit"
                    className={classes.submitButton}
                    disabled={inProgress as boolean}
                >
                    {t('screens:signIn.loginButton')}
                </CustomButton>
            </div>
            {inProgress && (
                <Loader dataQa={'login-in-progress'} absolutePosition={true}/>
            )}
        </form>
    );
};

export default LoginForm;
